import { Suspense, useEffect, type ReactNode } from "react";
import { useRouter, usePathname } from "@/lib/navigation";
import { useAuth } from "@/hooks/useAuth";
import { PermissionDenied } from "@/components/PermissionDenied";

// sessionStorage key written by /force-password-reset when the user clicks
// "Skip for now". The value is the grace deadline as an ISO string; we honour
// the skip only while the deadline is still in the future. The key dies with
// the tab, so re-login + close/reopen both force the prompt again.
const SKIP_KEY = "pwd_reset_skipped";

function shouldHonorForcedResetSkip(graceUntilIso: string | null): boolean {
  if (!graceUntilIso) return false;
  if (typeof window === "undefined") return false;
  const stored = (() => {
    try {
      return window.sessionStorage.getItem(SKIP_KEY);
    } catch {
      return null;
    }
  })();
  if (!stored) return false;
  const skipDeadline = new Date(stored);
  if (Number.isNaN(skipDeadline.getTime())) return false;
  return skipDeadline.getTime() > Date.now();
}

/** Who may see this subtree, judged on the effective user (the impersonated one when impersonating). */
export interface SessionPolicy {
  /** Where to send this user instead, or null to let them stay. */
  redirectFor?: (userType: string | undefined, pathname: string) => string | null;
  /** True to show the PermissionDenied screen instead of the page. */
  /**
   * Passed the EFFECTIVE user, not just its type: access is decided by the roles
   * and permissions WorkOS granted, and `userType` speaks only for the Partner
   * Portal — it cannot answer whether someone may open Issuing or the Merchant
   * portal.
   */
  deniedFor?: (user: { userType?: string; productRoles?: string[]; permissions?: string[] } | undefined, pathname: string) => boolean;
}

interface RequireSessionProps extends SessionPolicy {
  children: ReactNode;
}

function RequireSessionContent({ children, redirectFor, deniedFor }: RequireSessionProps) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Access is evaluated against the EFFECTIVE user — the impersonated user when
  // impersonating, otherwise the real one — so impersonation faithfully shows
  // what that user can (and cannot) reach.
  const effectiveUser = user?.impersonating ?? user;
  const effectiveType = effectiveUser?.userType;

  useEffect(() => {
    if (loading) return;

    if (!user) {
      const returnTo = pathname ? `?returnTo=${encodeURIComponent(pathname)}` : "";
      router.replace(`/login${returnTo}`);
      return;
    }

    // Forced password reset trumps everything else. Send the user to the
    // interstitial unless the skip is still valid (grace deadline in the
    // future + sessionStorage flag set). Impersonation is intentionally
    // exempt — an INTERNAL admin viewing the app as another user shouldn't
    // have to reset that user's password.
    if (
      user.mustResetPassword &&
      !user.impersonating &&
      pathname !== "/force-password-reset"
    ) {
      const skip = shouldHonorForcedResetSkip(user.tempPasswordGraceUntil);
      if (!skip) {
        const returnTo = pathname
          ? `?returnTo=${encodeURIComponent(pathname)}`
          : "";
        router.replace(`/force-password-reset${returnTo}`);
        return;
      }
    }

    const redirect = redirectFor?.(effectiveType, pathname);
    if (redirect) router.replace(redirect);
  }, [loading, user, effectiveType, pathname, router, redirectFor]);

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (deniedFor?.(effectiveUser ?? undefined, pathname)) return <PermissionDenied />;

  return <>{children}</>;
}

/** Signed in, past any forced password reset, and allowed by the policy; otherwise sent on. */
export default function RequireSession(props: RequireSessionProps) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      }
    >
      <RequireSessionContent {...props} />
    </Suspense>
  );
}
